import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (process.env.REACT_APP_IS_WIDGET === 'true') {
      const shadowHost = document.querySelector('#CDX-widget-shadow-host');
      if (shadowHost && shadowHost.shadowRoot) {
        const modalContainer = document.createElement('div');
        modalContainer.setAttribute('id', 'shadow-modal-container');
        shadowHost.shadowRoot.appendChild(modalContainer);
        setContainer(modalContainer);

        return () => {
          shadowHost.shadowRoot?.removeChild(modalContainer);
        };
      }
    } else {
      const widgetContainer = document.querySelector('.iLendingCDX__spa-container');
      if (widgetContainer) {
        setContainer(widgetContainer as HTMLElement);
      }
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const trapFocus = (e: FocusEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        modalRef.current.focus();
      }
    };

    if (isOpen && modalRef.current) {
      modalRef.current.focus();
      document.addEventListener('focusin', trapFocus);
    }

    return () => {
      document.removeEventListener('focusin', trapFocus);
    };
  }, [isOpen]);

  if (!isOpen || !container) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      className="iLendingCDX-modal-overlay"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className="iLendingCDX-modal"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="iLendingCDX-modal-header">
          <h2 id="iLendingCDX-modal-title">{title}</h2>
        </header>
        <div
          id="modal-description"
          className="iLendingCDX-modal-content"
        >
          {children}
        </div>
      </div>
    </div>,
    container
  );
};

export default Modal;
